import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import styles from "./RoomVideoPlayer.module.scss";
import { RoomMovie } from "../../../api/room";
import ArrowLeft from "../../../components/icons/chat/arrowLeft";
import ArrowRight from "../../../components/icons/chat/arrowRight";
import LikeIcon from "../../../components/icons/room/LikeIcon";
import DislikeIcon from "../../../components/icons/room/DislikeIcon";
import useInterfaceContext from "../../../components/providers/interface";
import MuteIcon from "../../../components/icons/room/MuteIcon";
import VolumeIcon from "../../../components/icons/room/VolumeIcon";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;
interface SlidesNavigation {
  mode: "slides";
  onNavigateUp: () => void;
  onNavigateDown: () => void;
  canNavigateUp: boolean;
  canNavigateDown: boolean;
}
interface ActionsNavigation {
  mode: "actions";
  movie: RoomMovie;
  onLike: () => void;
  onDislike: () => void;
  isLiked: boolean;
  isSwiping: boolean;
  formatLikeCount?: (count: number, options?: { compact?: boolean }) => string;
  onNext?: () => void;
  canGoNext?: boolean;
}

export type RoomVideoPlayerNavigation = SlidesNavigation | ActionsNavigation;

interface RoomVideoPlayerProps {
  trailerKey: string | null;
  backdropPath: string | null;
  muted: boolean;
  onToggleMuted: () => void;
  navigation: RoomVideoPlayerNavigation;
  isActive?: boolean;
  onTrailerError?: () => void;
}

const RoomVideoPlayer: React.FC<RoomVideoPlayerProps> = ({
                                                           trailerKey,
                                                           backdropPath,
                                                           muted,
                                                           onToggleMuted,
                                                           navigation,
                                                           isActive = true,
                                                           onTrailerError,
                                                         }) => {
  const [player, setPlayer] = useState<any>(null);
  const reactPlayerRef = useRef<ReactPlayer>(null);
  const [playing, setPlaying] = useState(false); // tracks actual playback, not desired state
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showControls, setShowControls] = useState(false);
  const controlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorHandledRef = useRef(false);
  const { isMobile } = useInterfaceContext();

  const url = trailerKey ? `https://www.youtube.com/watch?v=${trailerKey}` : "";

  useEffect(() => {
    errorHandledRef.current = false;
    setReady(false);
    setVideoStarted(false);
    setPlaying(false);
    setProgress(0);
    setDuration(0);
  }, [trailerKey]);

  const handlePlayerError = () => {
    if (errorHandledRef.current) return;
    errorHandledRef.current = true;
    onTrailerError?.();
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => setShowControls(false), 3000);
  };

  const handleMouseLeave = () => {
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    setShowControls(false);
  };

  const togglePlay = () => {
    if (!ready) return;
    const yt = player as any;
    if (playing) {
      yt?.pauseVideo?.();
      setPlaying(false);
    } else {
      yt?.playVideo?.();
      setPlaying(true);
    }
  };

  useEffect(() => {
    if (!player) return;
    if (muted) {
      (player as any)?.mute?.();
    } else {
      (player as any)?.unMute?.();
      (player as any)?.setVolume?.(100);
    }
  }, [player, muted]);

  useEffect(() => {
    if (!player) return;
    if (isActive) {
      (player as any)?.playVideo?.();
    } else {
      (player as any)?.pauseVideo?.();
      setPlaying(false);
    }
  }, [isActive, player]);

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!reactPlayerRef.current || duration <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const seekTo = pct * duration;
    reactPlayerRef.current.seekTo(seekTo, "seconds");
    setProgress(seekTo);
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const renderNavControls = () => {
    if (navigation.mode === "slides") {
      return (
        <div className={styles.navButtons}>
          <button
            className={styles.navBtn}
            onClick={navigation.onNavigateUp}
            disabled={!navigation.canNavigateUp}
            aria-label="Previous movie"
          >
            <ArrowLeft />
          </button>
          <button
            className={styles.navBtn}
            onClick={navigation.onNavigateDown}
            disabled={!navigation.canNavigateDown}
            aria-label="Next movie"
          >
            <ArrowRight />
          </button>
        </div>
      );
    }

    const { onLike, onDislike, isSwiping, isLiked, onNext, canGoNext } = navigation;

    return (
      <div className={styles.navButtons}>
        {isLiked && onNext ? (
          <button
            className={`${styles.navBtn} ${styles.navBtnNext}`}
            onClick={onNext}
            disabled={!canGoNext}
            aria-label="Next movie"
          >
            <ArrowRight />
          </button>
        ) : (
          <>
            <button
              className={`${styles.navBtn} ${isLiked ? styles.navBtnVoted : ""}`}
              onClick={onLike}
              disabled={isSwiping || isLiked}
              aria-label="Like"
            >
              <LikeIcon />
            </button>
            <button
              className={`${styles.navBtn} ${isLiked ? styles.navBtnVoted : ""}`}
              onClick={onDislike}
              disabled={isSwiping || isLiked}
              aria-label="Dislike"
            >
              <DislikeIcon />
            </button>
          </>
        )}
      </div>
    );
  };

  if (!trailerKey) {
    return (
      <div className={styles.playerWrapper}>
        <div className={styles.fallback}>
          {backdropPath ? (
            <img src={`${IMG_BASE}${backdropPath}`} alt="" className={styles.fallbackImg} />
          ) : (
            <div className={styles.noTrailer}>No trailer available</div>
          )}
        </div>
        {renderNavControls()}
      </div>
    );
  }

  return (
    <>
      <div
        className={styles.playerWrapper}
        ref={wrapperRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.playerInner}>
          <ReactPlayer
            ref={reactPlayerRef}
            url={url}
            playing={isActive}
            muted={muted}
            controls={false}
            width="100%"
            height="100%"
            playsinline={true}
            loop={true}
            onReady={(p: any) => {
              const internalPlayer = p.getInternalPlayer();
              setPlayer(internalPlayer);
              setReady(true);
              if (isActive) {
                internalPlayer?.playVideo?.();
              }
            }}
            onProgress={({ playedSeconds }: { playedSeconds: number }) =>
              setProgress(playedSeconds)
            }
            onDuration={(d: number) => setDuration(d)}
            onError={handlePlayerError}
            onPlay={() => { setVideoStarted(true); setPlaying(true); }}
            onPause={() => setPlaying(false)}
          />
        </div>

        {/* Cover until video starts — hides YouTube native UI, shows backdrop while loading */}
        {!videoStarted && (
          <div className={styles.loadingCover}>
            {backdropPath && (
              <img src={`${IMG_BASE}${backdropPath}`} alt="" className={styles.loadingCoverImg} />
            )}
          </div>
        )}

        {/* Gradient masks to hide YouTube overlays */}
        <div className={styles.ytMaskTop} />
        <div className={styles.ytMaskBottom} />

        {/* Transparent click overlay for play/pause */}
        <button
          type="button"
          tabIndex={-1}
          className={styles.videoToggle}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            togglePlay();
          }}
          aria-label={playing ? "Pause video" : "Play video"}
        />

        {/* Play button overlay */}
        {ready && !playing && (
          <div className={styles.playOverlay} onClick={togglePlay}>
            <svg width="55" height="55" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}

        {/* Progress bar */}
        {videoStarted && (showControls || !playing) && (
          <div className={styles.controls}>
            <span className={styles.time}>{formatTime(progress)}</span>
            <div
              className={styles.progressBar}
              onClick={(e) => { e.stopPropagation(); handleSeek(e); }}
            >
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ width: duration > 0 ? `${(progress / duration) * 100}%` : "0%" }}
                />
              </div>
            </div>
            <button
              className={styles.muteBtn}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleMuted();
              }}
            >
              {muted ? (
               <MuteIcon/>
              ) : (
                <VolumeIcon/>
              )}
            </button>
          </div>
        )}

        {!isMobile  && renderNavControls()}
      </div>

      {isMobile && navigation.mode === "actions" && renderNavControls()}
    </>
  );
};

export default RoomVideoPlayer;
